import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { IoCheckmark } from "react-icons/io5";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import TextTranslate from "../../../utils/TextTranslate";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import Cookies from "js-cookie";
import { getContrastTextColor, themeColors } from "../../../utils/themeColors";
import { useWebsiteSettings } from "../../../store/api/useWebsiteSettings";

const SelectThemeColorModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const ViewManager = useAppSelector((state) => state.ViewManager);
  const [color_id, setColorId] = useState(1);
  const { data: websiteSettings } = useWebsiteSettings();

  const handleCloseModal = () => {
    dispatch(
      updateViewState({
        show_select_theme_modal: false,
      }),
    );
  };

  useEffect(() => {
    const tempColor_id = Cookies.get("color_id");
    setColorId(tempColor_id ? Number(tempColor_id) : 1);
  }, [ViewManager.show_select_theme_modal]);

  function applyColor(hex: string, textColor: string, colorId?: number) {
    Cookies.set("theme_color", hex, { expires: 365 });
    Cookies.set("theme_text_color", textColor, { expires: 365 });
    if (colorId != null) {
      Cookies.set("color_id", colorId.toString(), { expires: 365 });
    }
    document.documentElement.style.setProperty("--theme-color", hex);
    document.documentElement.style.setProperty("--theme-text-color", textColor);
    handleCloseModal();
  }

  function resetColor() {
    const hex = websiteSettings?.data?.app_primary_color || "#FCC604";
    const text_color = getContrastTextColor(hex);
    Cookies.remove("color_id");
    applyColor(hex, text_color);
  }

  function selectColor() {
    const selectedColor = themeColors.find((c) => c.id === color_id);
    if (!selectedColor) return;
    applyColor(
      selectedColor.hex,
      getContrastTextColor(selectedColor.hex),
      color_id,
    );
  }

  return (
    <Dialog
      open={ViewManager.show_select_theme_modal}
      onClose={handleCloseModal}
      className="relative z-[100]"
    >
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm">
        <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4">
          <DialogPanel className="relative flex max-h-[85dvh] w-full max-w-md flex-col overflow-hidden rounded-t-[1.25rem] bg-primary shadow-2xl sm:rounded-2xl">
            <DialogTitle className="shrink-0 border-b border-black/[0.06] px-4 py-3.5 text-center text-[17px] font-semibold tracking-tight text-darkText dark:border-white/[0.08]">
              <TextTranslate text="Theme Color" />
            </DialogTitle>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
                <TextTranslate text="Colors" />
              </div>
              <div className="overflow-hidden rounded-2xl bg-secondary p-4 shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
                <div className="grid grid-cols-4 gap-4">
                  {themeColors.map((color) => {
                    const selected = color_id === color.id;
                    return (
                      <button
                        type="button"
                        key={color.id}
                        onClick={() => setColorId(color.id)}
                        aria-label={color.hex}
                        className="mx-auto grid place-content-center transition active:scale-95"
                      >
                        <span
                          className={`relative grid h-14 w-14 place-content-center rounded-full transition ${
                            selected
                              ? "ring-2 ring-themeColor ring-offset-2 ring-offset-secondary"
                              : "ring-1 ring-black/10 dark:ring-white/15"
                          }`}
                          style={{ backgroundColor: color.hex }}
                        >
                          {selected && (
                            <IoCheckmark
                              className="text-2xl drop-shadow-sm"
                              style={{
                                color: getContrastTextColor(color.hex),
                              }}
                            />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                onClick={resetColor}
                className="mt-4 w-full rounded-2xl bg-secondary py-3.5 text-center text-[17px] font-normal text-[#FF3B30] shadow-sm ring-1 ring-black/[0.04] transition active:bg-black/[0.03] dark:ring-white/[0.06] dark:active:bg-white/[0.04]"
              >
                <TextTranslate text="Reset color" />
              </button>
            </div>

            <div className="shrink-0 border-t border-black/[0.06] bg-primary/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl dark:border-white/[0.08]">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-xl bg-black/[0.06] py-3 text-[17px] font-medium text-darkText transition active:scale-[0.99] dark:bg-white/10"
                >
                  <TextTranslate text="Cancel" />
                </button>
                <button
                  type="button"
                  onClick={selectColor}
                  className="rounded-xl bg-themeColor py-3 text-[17px] font-semibold text-themeTextColor transition active:scale-[0.99]"
                >
                  <TextTranslate text="Select" />
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default SelectThemeColorModal;
